import { z } from 'zod';
import { endpoint } from './endpoint.js';
import {
  CampaignNumberPreviewQuerySchema,
  CampaignNumberPreviewSchema,
  CancelOfferSchema,
  CreateOfferSchema,
  ListOffersQuerySchema,
  OfferAssigneeSchema,
  OfferDetailSchema,
  OfferIdSchema,
  OfferListResponseSchema,
  OfferSchema,
  OfferShareRecordSchema,
  SalesUserSchema,
  ShareableRoleSchema,
  ShareableUserSchema,
  UpdateCampaignNumberSchema,
  UpdateOfferSchema,
  UpdateOfferSharesSchema,
} from '../schemas/offer.js';

export const offers = {
  list: endpoint('GET', '/offers', ListOffersQuerySchema, OfferListResponseSchema),
  // MUST be registered before ':id' server-side. NestJS matches static-suffix
  // routes only when declared first (see apps/api AGENTS.md route-ordering rule).
  listAssignableUsers: endpoint(
    'GET',
    '/offers/assignable-users',
    z.object({}),
    z.array(OfferAssigneeSchema),
  ),
  // MUST be registered before ':id' server-side, for the same reason as
  // listAssignableUsers above.
  listSalesUsers: endpoint('GET', '/offers/sales-users', z.object({}), z.array(SalesUserSchema)),
  // MUST be registered before ':id' server-side, for the same reason as
  // listAssignableUsers above.
  previewCampaignNumber: endpoint(
    'GET',
    '/offers/campaign-number-preview',
    CampaignNumberPreviewQuerySchema,
    CampaignNumberPreviewSchema,
  ),
  // Response carries the caller's own effectiveAccess and
  // hasUnsecuredAcceptedUnits on top of OfferSchema (offer-draft-foundation /
  // "Offer Read with Access Control").
  getById: endpoint('GET', '/offers/:id', z.object({ id: OfferIdSchema }), OfferDetailSchema),
  // Streams a .docx — not called through callEndpoint (see apps/web's raw-fetch
  // blob-download pattern). Response schema exists for path/param typing only;
  // do not model a parseable JSON body for the binary success case.
  getOrderDocument: endpoint(
    'GET',
    '/offers/:offerId/order-document',
    z.object({ offerId: OfferIdSchema }),
    z.object({}),
  ),
  create: endpoint('POST', '/offers', CreateOfferSchema, OfferSchema),
  update: endpoint(
    'PATCH',
    '/offers/:id',
    z.object({ id: OfferIdSchema }).merge(UpdateOfferSchema),
    OfferSchema,
  ),
  delete: endpoint('DELETE', '/offers/:id', z.object({ id: OfferIdSchema }), OfferSchema),
  cancel: endpoint(
    'POST',
    '/offers/:id/cancel',
    z.object({ id: OfferIdSchema }).merge(CancelOfferSchema),
    OfferSchema,
  ),
  updateCampaignNumber: endpoint(
    'PATCH',
    '/offers/:id/campaign-number',
    z.object({ id: OfferIdSchema }).merge(UpdateCampaignNumberSchema),
    OfferSchema,
  ),
  listShares: endpoint(
    'GET',
    '/offers/:id/shares',
    z.object({ id: OfferIdSchema }),
    z.array(OfferShareRecordSchema),
  ),
  updateShares: endpoint(
    'PUT',
    '/offers/:id/shares',
    z.object({ id: OfferIdSchema }).merge(UpdateOfferSharesSchema),
    z.array(OfferShareRecordSchema),
  ),
  listShareableUsers: endpoint(
    'GET',
    '/offers/:id/shareable-users',
    z.object({ id: OfferIdSchema }),
    z.array(ShareableUserSchema),
  ),
  listShareableRoles: endpoint(
    'GET',
    '/offers/:id/shareable-roles',
    z.object({ id: OfferIdSchema }),
    z.array(ShareableRoleSchema),
  ),
};
